import { Globe2, Shield, Users, Zap, Heart, Headphones, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getSupportEmail, openMailTo } from '../utils/obfuscate';

export default function AboutPage({ onBack }) {
  const { t } = useTranslation();

  const stats = [
    { value: '213+', label: t('about.stats.destinations'), icon: Globe2 },
    { value: '50K+', label: t('about.stats.customers'), icon: Users },
    { value: '4G/5G', label: t('about.stats.networkSpeed'), icon: Zap },
    { value: '24/7', label: t('about.stats.support'), icon: Headphones },
  ];

  const values = [
    { icon: Globe2, title: t('about.values.connectivity.title'), desc: t('about.values.connectivity.desc') },
    { icon: Zap, title: t('about.values.delivery.title'), desc: t('about.values.delivery.desc') },
    { icon: Shield, title: t('about.values.secure.title'), desc: t('about.values.secure.desc') },
    { icon: Heart, title: t('about.values.customer.title'), desc: t('about.values.customer.desc') }
  ];

  const testimonials = [
    { name: t('about.testimonials.t1.name'), location: t('about.testimonials.t1.location'), text: t('about.testimonials.t1.text'), rating: 5 },
    { name: t('about.testimonials.t2.name'), location: t('about.testimonials.t2.location'), text: t('about.testimonials.t2.text'), rating: 5 },
    { name: t('about.testimonials.t3.name'), location: t('about.testimonials.t3.location'), text: t('about.testimonials.t3.text'), rating: 5 },
    { name: t('about.testimonials.t4.name'), location: t('about.testimonials.t4.location'), text: t('about.testimonials.t4.text'), rating: 5 },
  ];

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {onBack && (
          <button onClick={onBack} className="text-slate-400 hover:text-white mb-6 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> {t('common.back')}
          </button>
        )}

        <div className="text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-bold text-white font-outfit mb-4"
            data-testid="about-title"
          >
            {t('about.title')} <span className="text-teal-400">eSIM4You</span>
          </motion.h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
            {t('about.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {stats.map((stat, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
              className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
              <stat.icon className="w-6 h-6 text-teal-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white font-mono">{stat.value}</p>
              <p className="text-slate-500 text-xs mt-1">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-16">
          <h2 className="text-2xl font-bold text-white mb-4">{t('about.storyTitle')}</h2>
          <div className="text-slate-400 text-sm leading-relaxed space-y-4">
            <p>{t('about.storyP1')}</p>
            <p>{t('about.storyP2')}</p>
            <p>{t('about.storyP3')}</p>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">{t('about.valuesTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {values.map((v, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <v.icon className="w-8 h-8 text-teal-400 mb-3" />
                <h3 className="text-white font-semibold mb-2">{v.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">{t('about.testimonialsTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {testimonials.map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: item.rating }).map((_, j) => (
                    <span key={j} className="text-amber-400 text-sm">&#9733;</span>
                  ))}
                </div>
                <p className="text-slate-300 text-sm leading-relaxed italic mb-4">"{item.text}"</p>
                <div>
                  <p className="text-white font-medium text-sm">{item.name}</p>
                  <p className="text-slate-500 text-xs">{item.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
          <h2 className="text-xl font-bold text-white mb-4">{t('about.companyTitle')}</h2>
          <div className="text-slate-400 text-sm space-y-1">
            <p>{t('about.companySubtitle')}</p>
            <p>{t('about.companyLocations')}</p>
            <p className="text-teal-400 mt-3 cursor-pointer hover:underline" onClick={openMailTo}>{getSupportEmail()}</p>
            <p className="text-slate-500 text-xs mt-2">{t('about.poweredBy')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
